import { useContext } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Page } from "@/ui/Layout";
import { AppContext } from "@/AppContext";
import { getBasicUserInfo } from "@/api/auth";
import { useUnifiedShell } from "@/access/useUnifiedShell";
import { LEGACY_TAB_PATHS, SettingsTab, settingsPath, settingsTabsFor } from "@/access/userTypes";
import { SettingsFrame } from "./SettingsFrame";
import { ProfilePanel } from "./UnifiedProfile";
import { DomainsDashboard } from "./DomainManagement/DomainsDashboard";
import { Integrations } from "./IntegrationManagement/Integrations";
import { ConnectClaude } from "@/Apps/Sites/ConnectClaude";

const useWho = () => {
  const { permissions = [] } = useContext(AppContext);
  return { email: getBasicUserInfo()?.email, permissions };
};

/**
 * /settings — the one Settings page for users without the Agents app (Agents users get the same
 * tabs inside /agents/settings). Switch off: sends people to the old Profile page.
 */
export function UnifiedSettings() {
  const unified = useUnifiedShell();
  const who = useWho();
  const location = useLocation();
  const navigate = useNavigate();
  if (!unified) return <Navigate to="/profile" replace />;
  const asked = new URLSearchParams(location.search).get("tab") as SettingsTab | null;
  // Permissions arrive a moment after the page opens: an Agents user may first land here, then move to Agents Settings on the same tab.
  if ((who.permissions || []).includes("agents")) return <Navigate to={settingsPath(who, asked || "profile")} replace />;

  const tabs = settingsTabsFor(who);
  const active: SettingsTab = asked && tabs.includes(asked) ? asked : tabs[0];

  return (
    <Page topBar={{ page: [{ label: "Settings" }] }}>
      <SettingsFrame tabs={tabs} active={active} onSelect={(t) => navigate(`/settings?tab=${t}`)}>
        {active === "profile" && <ProfilePanel />}
        {active === "domains" && <DomainsDashboard embedded />}
        {active === "connections" && <Integrations embedded />}
        {active === "claude" && <ConnectClaude embedded />}
      </SettingsFrame>
    </Page>
  );
}

/**
 * The old addresses (/profile, /domains, /integrations, /connect-claude). Switch off: the old page.
 * Switch on: the matching Settings tab, so bookmarks and the Launch Sites button still work.
 */
export function LegacyTab({ path, legacy }: { path: string; legacy: JSX.Element }) {
  const unified = useUnifiedShell();
  const who = useWho();
  const tab = LEGACY_TAB_PATHS[path];
  if (!unified || !tab || !settingsTabsFor(who).includes(tab)) return legacy;
  return <Navigate to={settingsPath(who, tab)} replace />;
}
